// src/pages/RawLogs.js
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../api/axiosInstance';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { FiFilter, FiAlertCircle, FiSearch, FiClock, FiFileText, FiAlertTriangle, FiDownload } from 'react-icons/fi';

const ITEMS_PER_PAGE = 100;

// --- Main Raw Logs Component ---
const RawLogs = () => {
    const [setBreadcrumb, setBreadcrumbLink] = useOutletContext();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // State for pagination, now driven by the API
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // State for date filters
    const [searchParams] = useSearchParams();

    // State for date filters
    const [startDate, setStartDate] = useState(() => {
        const paramStart = searchParams.get('startDate');
        if (paramStart) return paramStart;

        const date = new Date();
        date.setDate(date.getDate() - 7); // Default to 7 days ago
        return date.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        const paramEnd = searchParams.get('endDate');
        if (paramEnd) return paramEnd;

        return new Date().toISOString().split('T')[0];
    });

    // This is the single function responsible for fetching data for a specific page.
    const fetchLogs = async (start, end, page) => {
        setIsLoading(true);
        setError('');
        try {
            const response = await api.get(`/logs/datetime?startDate=${start}&endDate=${end}&page=${page}&limit=${ITEMS_PER_PAGE}`);
            setLogs(response.data.logs || []);
            setTotalPages(response.data.totalPages || 1);
            setCurrentPage(response.data.currentPage || 1);
        } catch (err) {
            setError(err.message === 'Network Error' ? 'No Internet Connection' : 'Failed to fetch logs.');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    // This useEffect now only runs once on component mount for the initial data load.
    useEffect(() => {
        setBreadcrumb(['Countdown', 'Analytics', 'Raw Logs']);
        setBreadcrumbLink(['/countdown', '/countdown/analytics']);
        fetchLogs(startDate, endDate, 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setBreadcrumb, setBreadcrumbLink]);

    // This handler explicitly calls the fetch function with the current date states.
    const handleFilterApply = () => {
        // Fetch page 1 of the new date range.
        fetchLogs(startDate, endDate, 1);
    };

    // This handler is for the pagination component to request a new page.
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            fetchLogs(startDate, endDate, newPage);
        }
    };

    // --- Function to handle exporting the current page's logs to CSV ---
    const handleExport = () => {
        if (logs.length === 0) {
            // In a real app, you might show a toast notification here
            alert("There are no logs on the current page to export.");
            return;
        }

        const headers = ["Timestamp", "Status", "IP Address", "Timer ID", "User Agent"];
        const rows = logs.map(log => [
            `"${new Date(log.timestamp).toLocaleString()}"`,
            log.status,
            log.ip,
            log.timerId,
            `"${log.userAgent.replace(/"/g, '""')}"` // Escape double quotes
        ].join(','));

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `logs_${startDate}_to_${endDate}_page_${currentPage}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // --- Memoized Stats for the current page ---
    const pageStats = useMemo(() => {
        if (!logs || logs.length === 0) {
            return { avgLatency: 0, totalRequests: 0, errorRate: 0 };
        }

        const totalRequests = logs.length;

        const totalLatency = logs.reduce((acc, log) => {
            const seconds = parseInt(log.latency.seconds, 10) || 0;
            const nanos = parseInt(log.latency.nanos, 10) || 0;
            const latencyInMs = (seconds * 1000) + (nanos / 1_000_000);
            return acc + latencyInMs;
        }, 0);
        const avgLatency = totalLatency / totalRequests;

        const errors = logs.filter(log => log.status >= 400).length;
        const errorRate = (errors / totalRequests) * 100;

        return { avgLatency, totalRequests, errorRate };
    }, [logs]);

    return (
        <div className="p-4 sm:p-6 bg-brand-background min-h-screen font-sans">
            {/* --- Header --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-brand-heading">Raw Request Logs</h1>
                    <p className="text-brand-paragraph mt-1">Inspect individual requests sent to your timers.</p>
                </div>
                <button onClick={handleExport} className="flex items-center gap-2 py-2 px-4 text-sm font-medium text-brand-heading bg-white border border-brand-border rounded-lg hover:bg-gray-50 transition-colors">
                    <FiDownload />
                    Export Page
                </button>
            </div>

            {/* --- Filter Section --- */}
            <div className="bg-white p-4 rounded-lg border border-brand-border shadow-sm mb-8 flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1 w-full">
                    <label htmlFor="startDate" className="block mb-2 text-sm font-medium text-gray-900">Start Date</label>
                    <input type="date" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" />
                </div>
                <div className="flex-1 w-full">
                    <label htmlFor="endDate" className="block mb-2 text-sm font-medium text-gray-900">End Date</label>
                    <input type="date" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} className="bg-gray-50 border border-brand-border text-gray-900 text-sm rounded-lg focus:ring-brand-accent focus:border-brand-accent block w-full p-2.5" />
                </div>
                <div className="w-full sm:w-auto pt-0 sm:pt-7">
                    <button onClick={handleFilterApply} className="w-full flex items-center justify-center gap-2 text-white bg-brand-accent hover:bg-blue-700 font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                        <FiFilter /> Apply
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div className="text-center p-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent mx-auto"></div></div>
            ) : error ? (
                <div className="p-8 text-center text-red-500 bg-red-50 rounded-lg"><FiAlertCircle className="mx-auto text-4xl mb-2" />{error}</div>
            ) : (
                <>
                    {/* --- Visual Stats Section --- */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                        <StatCard
                            icon={<FiFileText size={20} className="text-blue-500" />}
                            title="Total Requests (Page)"
                            value={pageStats.totalRequests.toLocaleString()}
                            colorClass="bg-blue-100"
                        />
                        <StatCard
                            icon={<FiClock size={20} className="text-yellow-600" />}
                            title="Avg Latency (Page)"
                            value={`${pageStats.avgLatency.toFixed(2)} ms`}
                            colorClass="bg-yellow-100"
                        />
                        <StatCard
                            icon={<FiAlertTriangle size={20} className="text-red-500" />}
                            title="Error Rate (Page)"
                            value={`${pageStats.errorRate.toFixed(1)}%`}
                            colorClass="bg-red-100"
                        />
                    </div>

                    {/* --- Logs Table --- */}
                    <div className="bg-white rounded-lg border border-brand-border shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left text-brand-paragraph">
                                <thead className="text-xs text-brand-heading uppercase bg-gray-50">
                                    <tr>
                                        <th scope="col" className="py-3 px-6">Timestamp</th>
                                        <th scope="col" className="py-3 px-6">Status</th>
                                        <th scope="col" className="py-3 px-6">IP Address</th>
                                        <th scope="col" className="py-3 px-6">Timer ID</th>
                                        <th scope="col" className="py-3 px-6">User Agent</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.length > 0 ? logs.map(log => (
                                        <LogTableRow key={log._id} log={log} />
                                    )) : (
                                        <tr>
                                            <td colSpan="5" className="text-center py-16">
                                                <FiSearch className="mx-auto text-4xl mb-2 text-gray-300" />
                                                <h3 className="text-lg font-semibold">No Logs Found</h3>
                                                <p>Try adjusting your date range.</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

// --- Child Component: StatCard ---
const StatCard = ({ icon, title, value, colorClass }) => (
    <div className="bg-white p-5 rounded-lg border border-brand-border flex items-center gap-4">
        <div className={`p-3 rounded-full ${colorClass}`}>
            {icon}
        </div>
        <div>
            <p className="text-2xl font-bold text-brand-heading">{value}</p>
            <p className="text-sm text-brand-paragraph">{title}</p>
        </div>
    </div>
);

// --- Child Component: LogTableRow ---
const LogTableRow = ({ log }) => {
    const getStatusColor = (status) => {
        if (status >= 500) return 'bg-red-100 text-red-800';
        if (status >= 400) return 'bg-yellow-100 text-yellow-800';
        if (status >= 300) return 'bg-blue-100 text-blue-800';
        return 'bg-green-100 text-green-800';
    };

    return (
        <tr className="bg-white border-b border-gray-100 hover:bg-gray-50">
            <td className="py-4 px-6 font-medium text-brand-heading">{new Date(log.timestamp).toLocaleString()}</td>
            <td className="py-4 px-6">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(log.status)}`}>
                    {log.status}
                </span>
            </td>
            <td className="py-4 px-6 font-mono">{log.ip}</td>
            <td className="py-4 px-6 font-mono">{log.timerId}</td>
            <td className="py-4 px-6 text-gray-500 truncate" title={log.userAgent} style={{ maxWidth: '300px' }}>{log.userAgent}</td>
        </tr>
    );
};

// --- Child Component: Pagination ---
const Pagination = ({ currentPage, totalPages, onPageChange }) => (
    <div className="p-4 flex justify-between items-center text-sm text-brand-paragraph border-t border-gray-100">
        <span>Page {currentPage} of {totalPages}</span>
        <div className="inline-flex items-center space-x-2">
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="py-2 px-3 leading-tight bg-white border border-brand-border rounded-lg hover:bg-gray-100 disabled:opacity-50">Previous</button>
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="py-2 px-3 leading-tight bg-white border border-brand-border rounded-lg hover:bg-gray-100 disabled:opacity-50">Next</button>
        </div>
    </div>
);

export default RawLogs;
